import type { ModelPortfolioTradeAlertLink } from "../entities";
import type { ModelPortfolio, ModelPortfolioConfiguration, ModelPortfolioData, ModelPortfolioTradeAlert } from "../portfolio";
import { PositionMetrics } from "./my-portfolios";

export type ModelPortfolios = {
    modelPortfoliosByUserAccess: {
        portfolios: Portfolio []
    }
}

export type WatchlistPosition = {
    pos_id: number,
    metrics: PositionMetrics
}

export type MyWatchlist = {
    watchlist_id: number,
    positions: WatchlistPosition[]
}

export type WatchlistApiResponse = {
    watchlist: MyWatchlist
}

export type Position = {
    symbol: string,
    stance?: string | undefined | null,
    sector?: string | undefined | null,
    last_close?: number | undefined | null,
    bpi_status?: string | undefined | null,
    sector_rs?: string | undefined | null,
    peer_rs?: string | undefined | null,
    mkt_rs?: string | undefined | null,
    initiated?: string | undefined | null,
    last_check_point?: string | undefined | null,

    opened_stock_price?: number | undefined | null,
    price_target?: number | undefined | null,
    stop_loss?: number | undefined | null,
    opened_options_price?: number | undefined | null,

    closed?: string | undefined | null,
    closed_stock_price?: number | undefined | null,
    stock_gains?: number | undefined | null,
    month_end_gain?: string | undefined | null,
    max_gain?: string | undefined | null,
    closed_options_price?: number | undefined | null,
    options_gains?: number | undefined | null,

    note?: string | undefined | null,

    alert_history?: ModelPortfolioTradeAlertLink[] | undefined | null
}

export type PositionData = {
    open_pos: Position [],
    closed_pos: Position[],
    stock_gains?: number,
    options_gains?: number
}

export type Portfolio = {
    key: string,
    metadata: PortfolioMetadata,
    data: PositionData
}

export type PortfolioMetadata = {
    name: string,
    description: string,
    open_pos_keys: string[],
    closed_pos_keys: string[]
}

export type SectorAlertApiResponse = {
    kind: number[],
    top_stock_1: string,
    top_stock_2: string,
    top_stock_3: string,
    top_etf_1: string,
    top_etf_2: string,
    top_etf_3: string,
    bot_stock_1: string,
    bot_stock_2: string,
    bot_stock_3: string,
    bot_etf_1: string,
    bot_etf_2: string,
    bot_etf_3: string
}

export type Sector = {
    id: number,
    name: string,
    symbol: string,
    indexChartId: number,
    etfCount: number,
    stockCount: number
}

export type GetSectorsResponse = {
    sectors:{
        items: Sector[]
    }
}

export type SectorHistoryApiResponse = {
    sectorHistoryForDates: {
        items: SectorHistoryForDate[]
    }
}

export type SectorHistoryForDate = {
    dt: string,
    history: SectorStatsApiResponse[]
}

export type SectorStatsApiResponse = {
    sector_id: number,
    symbol: string,

    // The equally weighted sector p&f stats (log return — rehydrated to cumulative value client-side)
    idx_log_rtn: number,
    idx_sig: string,
    dt_last_idx_sig: string | null | undefined,
    idx_col: string,
    dt_last_idx_col: string | null | undefined,

    // The sector's BPI p&f stats
    bpi_val: number,
    bpi_sig: string,
    dt_last_bpi_sig: string | null | undefined,
    bpi_col: string,
    dt_last_bpi_col: string | null | undefined,

    // The sector's %30 week p&f stats
    p30_val: number,
    p30_sig: string,
    dt_last_p30_sig: string | null | undefined,
    p30_col: string,
    dt_last_p30_col: string | null | undefined,

    // The sector's %10 week p&f stats
    p10_val: number,
    p10_sig: string,
    dt_last_p10_sig: string | null | undefined,
    p10_col: string,
    dt_last_p10_col: string | null | undefined,

    // The sector's relative strength p&f stats (log return — rehydrated to cumulative value client-side)
    rs_rank: number,
    rs_log_rtn: number,
    rs_sig: string,
    dt_last_rs_sig: string | null | undefined,
    rs_col: string,
    rs_chg_dt: string | null | undefined,

    // The alerts associated with this sector on the given date
    alerts: SectorAlertApiResponse
}

/****************************************************************************/

export function createPortfolio( portfolioKey: string,
                                 configuration: PortfolioMetadata,
                                 data: PositionData ): ModelPortfolio {
    return {
        key: portfolioKey,
        data: adaptPositionData( data ),
        configuration: adaptConfiguration( configuration )
    }
}

function adaptConfiguration( configuration: PortfolioMetadata ): ModelPortfolioConfiguration {
    return {
        name: configuration.name,
        description: configuration.description,
        openPositionKeyList: configuration.open_pos_keys,
        closedPositionKeyList: configuration.closed_pos_keys
    };
}

function adaptPositionData( data: PositionData ): ModelPortfolioData {
    const openPositions: ModelPortfolioTradeAlert[] = buildModelPortfolioPositions( data.open_pos );
    const closedPositions: ModelPortfolioTradeAlert[] = buildModelPortfolioPositions( data.closed_pos );

    return {
        positions: {
            opened: openPositions,
            closed: closedPositions
        }
    };
}

export function buildModelPortfolioPositions( data: Position[] ): ModelPortfolioTradeAlert[] {
    let recommendations: ModelPortfolioTradeAlert[] = [];
    try {
        recommendations = data.map( ( item: Position ) => {
            return {
                symbol: item.symbol,
                initiated: item.initiated,
                opened_stock_price: item.opened_stock_price,
                opened_options_price: item.opened_options_price,
                last_close: item.last_close,
                alert_history: item.alert_history,
                note: item.note,

                // Sector metrics
                sector: item.sector,
                bpi_status: item.bpi_status,
                sector_rs: item.sector_rs,
                peer_rs: item.peer_rs,
                mkt_rs: item.mkt_rs,
                stance: item.stance,

                // CPT-specific dates
                last_check_point: item.last_check_point,

                // MTT-specific performance metrics
                m_e_gain: item.month_end_gain,
                max_gain: item.max_gain,

                // Price/Trade metrics
                stop_loss: item.stop_loss,
                price_target: item.price_target,

                // Close metrics
                closed: item.closed,
                closed_stock_price: item.closed_stock_price,
                stock_gains: item.stock_gains,
                closed_options_price: item.closed_options_price,
                options_gains: item.options_gains,
            } as ModelPortfolioTradeAlert;
        } );
    }
    catch ( err: any ) {
        console.warn( JSON.stringify( err.message ) );
    }

    return recommendations;
}
